import { useMemo } from 'react';
import { StatsMetricProps } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { featureFlags } from '../../../../feature-flags';
import { RoiSummary } from '../../components/roi-summary';
import { AROverviewData } from '../types';

interface ARSummaryProps {
  data: AROverviewData;
  isLoading?: boolean;
}

export const ARSummary = ({ data, isLoading }: ARSummaryProps) => {
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.missedCallTextRoiDemoData);

  const summaryCardsData = useMemo<StatsMetricProps[]>(() => {
    return [
      {
        uniqueId: 'appointment-reminders-sent',
        title: t('Appointment Reminders Sent'),
        data: [
          {
            label: t('{{count}} Patients', { count: data.remindersSent.patientsCount }),
            value: formatters.value(data.remindersSent.remindersCount),
            infoHoverText: t('{{messages}} Messages were sent to {{patients}} Patients', {
              messages: data.remindersSent.remindersCount,
              patients: data.remindersSent.patientsCount,
            }),
          },
        ],
      },
      {
        uniqueId: 'appointment-reminders-confirmed',
        title: t('Appointments Confirmed'),
        data: [
          {
            label: t('{{count}} Patients', { count: data.appointmentsConfirmed.patientsCount }),
            value: formatters.value(data.appointmentsConfirmed.appointmentsCount),
            infoHoverText: t('A total of {{appointments}} appointments were confirmed by {{patients}} patients', {
              appointments: data.appointmentsConfirmed.appointmentsCount,
              patients: data.appointmentsConfirmed.patientsCount,
            }),
          },
        ],
      },
    ];
  }, [data, t]);

  return (
    <RoiSummary
      isDemoAccount={isDemoAccount}
      isLoading={isLoading}
      productionAppointmentsCount={data.appointmentsCompleted.appointmentsCompletedByReminders}
      productionPatientsCount={data.appointmentsCompleted.appointmentsCompletedPatients}
      summaryCardsData={summaryCardsData}
    />
  );
};
